// ============================================================
// TMR Portal Admin — peças comuns das telas de crédito (26)
// ============================================================
import { useState } from 'react';
import type { ReactNode } from 'react';
import { STATUS_MAP } from '../../components/primitives';
import { Pagination, usePagination } from '../../components/Pagination';
import { FilterTabs, Table, Td } from '../../components/ui';
import {
  clinicLabel,
  dayLabel,
  daysLateLabel,
  hasRestriction,
  installmentLabel,
  money,
  negativeLabel,
  negativeShortLabel,
  occurrenceRows,
  placeLabel,
  situationLabel,
  situationTone,
} from '../../data/credit/status';
import type { BureauNegativeView, BureauReportView, DebtsView } from '../../data/credit/contract';
import { formatDay } from '../../data/businessRules/status';

const TONE_STYLE = {
  ok: STATUS_MAP.pago,
  warn: STATUS_MAP.pendente,
  bad: STATUS_MAP.recusado,
  neutral: { bg: 'var(--stone-100)', fg: 'var(--stone-600)', label: '' },
};

/** The situation of an evaluation as a pill, with the credit screens' own wording. */
export function SituationBadge({ situation }: { situation: string }) {
  const style = TONE_STYLE[situationTone(situation)];
  return (
    <span
      style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 11.5,
        fontWeight: 500,
        padding: '3px 9px',
        borderRadius: 999,
        background: style.bg,
        color: style.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {situationLabel(situation)}
    </span>
  );
}

/**
 * A titled block of the detail and the lookup: the heading and its aside on one line, the content below. It is not
 * a card itself — tables are already one, and the content decides its own surface, so no box sits inside another.
 */
export function Section({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', minHeight: 32 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, margin: 0, color: 'var(--ink-900)' }}>{title}</h2>
        {aside && <div style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{aside}</div>}
      </div>
      {children}
    </section>
  );
}

/** A padded card — `.pa-card` is only surface, every use gives it its padding. */
export function Panel({ children }: { children: ReactNode }) {
  return (
    <div className="pa-card" style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {children}
    </div>
  );
}

/** A row of figures in even columns, so labels and values line up whatever their length. */
export function Figures({ children }: { children: ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '14px 24px' }}>{children}</div>;
}

/** One figure with its label, for the summaries. */
export function Figure({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span className="pa-eyebrow">{label}</span>
      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, color: 'var(--ink-900)' }}>{value}</span>
    </div>
  );
}

/** A line of secondary text inside a panel, set off from the figures above it. */
export function PanelNote({ children }: { children: ReactNode }) {
  return (
    <div style={{ paddingTop: 12, borderTop: '1px solid var(--border-default)', fontSize: 13.5, lineHeight: 1.55, color: 'var(--fg-muted)' }}>
      {children}
    </div>
  );
}

/** A section's quiet message when it has nothing to show. */
export function Muted({ children }: { children: ReactNode }) {
  return <p style={{ margin: 0, fontSize: 14, color: 'var(--fg-muted)' }}>{children}</p>;
}

/** How many rows a long credit table shows at a time. */
export const CREDIT_PAGE_SIZE = 10;

/**
 * A document's debts: the sums, then the installments, oldest due date first and {@link CREDIT_PAGE_SIZE} at a time —
 * the delinquent ones in the danger tone, since they are what held the sale back.
 */
export function DebtsTable({ debts, emptyMessage }: { debts: DebtsView; emptyMessage: string }) {
  const { summary } = debts;
  const pages = usePagination(debts.installments, CREDIT_PAGE_SIZE);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Panel>
        <Figures>
          <Figure label="Em aberto" value={`${summary.count} · ${money(summary.amount)}`} />
          <Figure label="Em dia" value={`${summary.upToDateCount} · ${money(summary.upToDateAmount)}`} />
          <Figure label="Inadimplentes" value={`${summary.delinquentCount} · ${money(summary.delinquentAmount)}`} />
          <Figure label="Maior atraso" value={summary.longestDelayDays === 0 ? '—' : `${summary.longestDelayDays} dias`} />
        </Figures>
      </Panel>

      {debts.installments.length === 0 ? (
        <Muted>{emptyMessage}</Muted>
      ) : (
        <>
          <Table
            dense
            columns={[
              { label: 'Parcela' },
              { label: 'Clínica' },
              { label: 'Vencimento' },
              { label: 'Valor', align: 'right' },
              { label: 'Situação' },
            ]}
          >
            {pages.pageItems.map((debt) => (
              <tr key={`${debt.orderId}-${debt.installment}-${debt.dueDate}`}>
                <Td dense>{installmentLabel(debt)}</Td>
                <Td dense>{clinicLabel(debt.clinic)}</Td>
                <Td dense>{dayLabel(debt.dueDate)}</Td>
                <Td dense align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{money(debt.amount)}</Td>
                <Td dense style={{ color: debt.delinquent ? 'var(--danger)' : 'var(--fg-muted)', fontWeight: debt.delinquent ? 600 : 400 }}>
                  {debt.delinquent ? `Inadimplente · ${daysLateLabel(debt)}` : daysLateLabel(debt)}
                </Td>
              </tr>
            ))}
          </Table>
          <Pagination page={pages.page} pageCount={pages.pageCount} total={pages.total} onChange={pages.setPage} />
        </>
      )}
    </div>
  );
}

/**
 * A Serasa report as the TMR reads it: the score and the registration on top, then every restriction the combo
 * brought, in tables. A consult kept before the complete response was stored says so, rather than reading as "no
 * restriction".
 */
export function BureauReportCard({ report }: { report: BureauReportView }) {
  const restrictions = report.negatives.filter(hasRestriction);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Panel>
        <Figures>
          <Figure label="Score" value={report.score ?? '—'} />
          <Figure label="Faixa" value={report.scoreRange ?? '—'} />
          <Figure label="Prob. de inadimplência" value={report.defaultProbability ?? '—'} />
          <Figure label="Consultado em" value={formatDay(report.consultedAt)} />
        </Figures>
        <PanelNote>
          <div style={{ color: 'var(--ink-900)' }}>
            {report.name ?? 'Nome não informado'}
            {report.birthDate && ` · nascido em ${dayLabel(report.birthDate)}`}
            {report.registrationStatus && ` · CPF ${report.registrationStatus.toLowerCase()}`}
          </div>
          {!report.complete ? (
            <div style={{ marginTop: 4, color: 'var(--fg-subtle)' }}>
              Consulta guardada antes de 29/09/2026: só o cadastro e o score ficaram registrados.
            </div>
          ) : restrictions.length === 0 ? (
            <div style={{ marginTop: 4 }}>Nenhuma restrição no relatório.</div>
          ) : (
            <div style={{ marginTop: 4 }}>
              {restrictions.map((negative) => `${negativeShortLabel(negative.kind)} ${negative.count ?? negative.occurrences.length}`).join(' · ')}
            </div>
          )}
        </PanelNote>
      </Panel>

      {report.complete && report.negatives.length > 0 && <NegativesSummaryTable negatives={report.negatives} />}
      {report.complete && restrictions.length > 0 && <OccurrencesTable report={report} />}
    </div>
  );
}

/**
 * One line per kind of restriction the report brought — Pefin, Refin, protests, checks… —, with how many, how much,
 * and the first and the last occurrence. A kind with nothing in it stays, muted: "nada consta" is an answer too.
 */
export function NegativesSummaryTable({ negatives }: { negatives: BureauNegativeView[] }) {
  return (
    <Table
      dense
      columns={[
        { label: 'Restrição' },
        { label: 'Ocorrências', align: 'right' },
        { label: 'Valor', align: 'right' },
        { label: 'Primeira ocorrência' },
        { label: 'Última ocorrência' },
      ]}
    >
      {negatives.map((negative) => {
        const empty = !hasRestriction(negative);
        const muted = empty ? { color: 'var(--fg-subtle)' } : undefined;
        return (
          <tr key={negative.kind}>
            <Td dense style={{ fontWeight: empty ? 400 : 600, ...muted }}>{negativeLabel(negative)}</Td>
            <Td dense align="right" style={{ fontVariantNumeric: 'tabular-nums', ...muted }}>
              {empty ? 'Nada consta' : negative.count ?? negative.occurrences.length}
            </Td>
            <Td dense align="right" style={{ fontVariantNumeric: 'tabular-nums', ...muted }}>{empty ? '—' : money(negative.amount)}</Td>
            <Td dense style={muted}>{dayLabel(negative.firstOccurrence)}</Td>
            <Td dense style={muted}>{dayLabel(negative.lastOccurrence)}</Td>
          </tr>
        );
      })}
    </Table>
  );
}

/**
 * Each occurrence of the report, newest first and {@link CREDIT_PAGE_SIZE} at a time, filtered by kind. A report
 * whose sections brought only the totals says so instead of an empty table.
 */
export function OccurrencesTable({ report }: { report: Pick<BureauReportView, 'negatives'> }) {
  const [kind, setKind] = useState('all');
  const rows = occurrenceRows(report);
  const kinds = report.negatives.filter((negative) => negative.occurrences.length > 0);
  const shown = kind === 'all' ? rows : rows.filter((row) => row.kind === kind);
  const pages = usePagination(shown, CREDIT_PAGE_SIZE);

  if (rows.length === 0) {
    return <Muted>O relatório trouxe só os totais, sem o detalhe de cada ocorrência.</Muted>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {kinds.length > 1 && (
        <FilterTabs
          value={kind}
          onChange={(value) => {
            setKind(value);
            pages.setPage(1);
          }}
          tabs={[
            { value: 'all', label: 'Todas', count: rows.length },
            ...kinds.map((negative) => ({ value: negative.kind, label: negativeShortLabel(negative.kind), count: negative.occurrences.length })),
          ]}
        />
      )}
      <Table
        dense
        columns={[
          { label: 'Data' },
          { label: 'Tipo' },
          { label: 'Credor / origem' },
          { label: 'Natureza' },
          { label: 'Contrato' },
          { label: 'Local' },
          { label: 'Valor', align: 'right' },
        ]}
      >
        {pages.pageItems.map((row, index) => (
          <tr key={`${row.kind}-${row.date}-${row.contract}-${index}`}>
            <Td dense style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{dayLabel(row.date)}</Td>
            <Td dense>
              <KindTag kind={row.kind} />
            </Td>
            <Td dense style={{ fontWeight: 500 }}>{row.creditor ?? '—'}</Td>
            <Td dense style={{ color: 'var(--fg-muted)' }}>{row.nature ?? '—'}</Td>
            <Td dense style={{ color: 'var(--fg-muted)', fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{row.contract ?? '—'}</Td>
            <Td dense style={{ color: 'var(--fg-muted)' }}>{placeLabel(row)}</Td>
            <Td dense align="right" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(row.amount)}</Td>
          </tr>
        ))}
      </Table>
      <Pagination page={pages.page} pageCount={pages.pageCount} total={pages.total} onChange={pages.setPage} />
    </div>
  );
}

/** The kind of a restriction as a small tag, so the column reads at a glance. */
function KindTag({ kind }: { kind: string }) {
  return (
    <span
      style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 11.5,
        fontWeight: 600,
        padding: '3px 8px',
        borderRadius: 999,
        background: 'var(--stone-100)',
        color: 'var(--stone-600)',
        whiteSpace: 'nowrap',
      }}
    >
      {negativeShortLabel(kind)}
    </span>
  );
}
